import { Component, computed, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSelectChange } from '@angular/material/select';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { OpenService, SelectableOption } from '@app-services/open.service';
import { EvaluationService } from '@app-services/evaluation.service';
import { SessionService } from '@app/store/session/session.service';
import { Permission, UserInfo } from '@app/store/session/session.store';
import {
  EvaluationVehicleFilter,
  EvaluationStatusSelectedOptions,
  EvalTypeLabel,
  OwnerTypeLabel,
  NegotiationStatusTypes,
  FastQuoteStatus
} from '@app-model/evaluation';
import { EvalRescueStatus, EVAL_RESCUE_STATUS_LABELS } from '@app-model/eval-rescue';
import { SegmentLabel } from '@app-model/vehicle';
import { EstadosSiglaLabel } from '@app-model/state';
import { City } from '@app-model/city';
import { EvalDealerHelper } from '@app/views/private/admin/model/advertisers';
import { NgxDaterangepickerBootstrapDirective } from 'ngx-daterangepicker-bootstrap';
import dayjs, { Dayjs } from 'dayjs';
import { IdName } from '@app-services/oem-dealer.service';

@Component({
  selector: 'app-filter-evaluation-vehicles',
  templateUrl: './filter-evaluation-vehicles.component.html',
  styleUrls: ['./filter-evaluation-vehicles.component.scss']
})
export class FilterEvaluationVehiclesComponent implements OnInit, OnChanges {
  // Injeção de dependências
  private readonly sessionService = inject(SessionService);
  private readonly evaluationService = inject(EvaluationService);
  private readonly openService = inject(OpenService);
  private readonly destroyRef = inject(DestroyRef);

  // Inputs e Outputs - usando decorators clássicos para compatibilidade com change detection
  @Input() filter!: EvaluationVehicleFilter;
  @Input() showFinishedStatus = false;
  @Output() readonly filterChanged = new EventEmitter<EvaluationVehicleFilter>();

  // Signals para estado
  readonly userInfo = signal<UserInfo | undefined>(undefined);
  readonly statusOptions = signal<SelectableOption<EvaluationStatusSelectedOptions>[]>([]);
  readonly allStatusNegotiation = signal<string[]>(NegotiationStatusTypes.allTypes);
  readonly evalDealerOptions = signal<EvalDealerHelper[]>([]);
  readonly cityOptions = signal<City[]>([]);
  readonly selectedCities = signal<City[]>([]);

  // Constantes
  readonly allSegments = SegmentLabel.allSegments;
  readonly allEvalTypes = EvalTypeLabel.allTypes;
  readonly allOwnerTypes = OwnerTypeLabel.allTypes;
  readonly allFastQuoteOfferStatus = FastQuoteStatus.allTypesOffer;
  readonly allFastQuoteNoOfferStatus = FastQuoteStatus.allTypeNoOffer;
  readonly rescueSentToStoreStatus = EvalRescueStatus.SENT_TO_STORE;
  readonly rescueSentToStoreLabel = EVAL_RESCUE_STATUS_LABELS[EvalRescueStatus.SENT_TO_STORE];
  readonly states = EstadosSiglaLabel;

  // Form Controls
  readonly evalDealerControl = new FormControl<string>('');
  readonly cityControl = new FormControl<string>('');
  readonly ufControl = new FormControl<string[]>([]);

  // Computed values
  readonly canShowFilterStatus = computed(() => {
    const user = this.userInfo();
    return user?.hasAnyPermissionOrLegacy(Permission.VIEW_EVALUATION_STATUS_FILTERS) ?? false;
  });

  readonly canShowPricingUserFilter = computed(() => this.userInfo()?.canViewPricingUserFilter() ?? false);

  readonly pricingUserOptions = signal<IdName[]>([]);
  readonly pricingUserControl = new FormControl<string[]>([]);

  selectedDateRange: { startDate: Dayjs | null; endDate: Dayjs | null } | null = null;

  readonly ranges: Record<string, [Dayjs, Dayjs]> = {
    'Hoje': [dayjs().startOf('day'), dayjs().endOf('day')],
    'Ontem': [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
    'Últimos 7 dias': [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')],
    'Últimos 30 dias': [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')],
    'Mês atual': [dayjs().startOf('month'), dayjs().endOf('day')],
    'Mês passado': [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  };

  @ViewChild(NgxDaterangepickerBootstrapDirective) private readonly periodPicker?: NgxDaterangepickerBootstrapDirective;

  get periodRange(): string {
    if (this.selectedDateRange?.startDate && this.selectedDateRange?.endDate) {
      return `${this.selectedDateRange.startDate.format('DD/MM/YYYY')} - ${this.selectedDateRange.endDate.format('DD/MM/YYYY')}`;
    }
    return '';
  }

  get hasPeriodSelected(): boolean {
    return !!(this.selectedDateRange?.startDate && this.selectedDateRange?.endDate);
  }

  get isFastQuoteSelected(): boolean {
    return (this.filter.statuses?.includes('FAST_QUOTE')) ||
           (this.filter.fastQuoteStatuses && this.filter.fastQuoteStatuses.length > 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filter && !changes.filter.firstChange) {
      this.initStatusOptions();
      this.refreshPeriodRange();
      this.refreshPricingUserControl();
    }
  }

  ngOnInit(): void {
    this.setupUserAndLayoutVars();
    this.setupFormControlListeners();
    this.initializeFromFilter();
    this.refreshPeriodRange();
  }

  private setupUserAndLayoutVars(): void {
    this.sessionService.getUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.userInfo.set(user);
        this.loadPricingUsers(user);
        this.initFilterOptions();

        this.openService.getLayoutVars()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(layoutVars => {
            const isCpfl = layoutVars?.alternativeDomain?.includes('gestaocpfl');
            const filtered = isCpfl
              ? NegotiationStatusTypes.allTypes.filter(s => !['HOT', 'TEPID', 'COLD'].includes(s))
              : NegotiationStatusTypes.allTypes.filter(s => ['HOT', 'TEPID', 'COLD', 'NO_STATUS', 'DEAL_DONE'].includes(s));
            this.allStatusNegotiation.set(filtered);
          });
      });
  }

  private loadPricingUsers(user?: UserInfo): void {
    if (!user?.canViewPricingUserFilter()) {
      this.filter.pricingUserIds = undefined;
      this.refreshPricingUserControl();
      return;
    }

    this.evaluationService
      .getPricingUsers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((users) => this.pricingUserOptions.set(users ?? []));
  }

  private setupFormControlListeners(): void {
    this.evalDealerControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(query => this.onChangeEvalDealerQuery(query));

    this.cityControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => this.onChangeCityQuery(val ?? ''));
  }

  private initializeFromFilter(): void {
    if (this.filter.ufs?.length) {
      this.ufControl.setValue(this.filter.ufs);
    }

    if (this.filter.cityIds?.length) {
      this.openService.getCity(this.filter.cityIds[0])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(city => {
          this.cityControl.setValue(`${city.name} - ${city.uf}`);
        });
    }

    this.refreshPricingUserControl();
  }

  private refreshPricingUserControl(): void {
    this.pricingUserControl.setValue(this.filter.pricingUserIds ?? []);
  }

  private initFilterOptions(): void {
    this.initStatusOptions();
    const user = this.userInfo();
    if (user && !user.isAdmin()) {
      this.refreshEvalDealerOptions();
    }
  }

  private initStatusOptions(): void {
    const options: SelectableOption<EvaluationStatusSelectedOptions>[] = [];
    const selectedStatuses = this.filter.statuses ?? [];

    Object.keys(EvaluationStatusSelectedOptions).forEach(key => {
      const value = EvaluationStatusSelectedOptions[key as keyof typeof EvaluationStatusSelectedOptions];
      if (value !== '') {
        const option = new SelectableOption<EvaluationStatusSelectedOptions>();
        option.option = value;
        option.selected = selectedStatuses.includes(key);
        options.push(option);
      }
    });

    this.statusOptions.set(options);
  }

  private refreshEvalDealerOptions(): void {
    this.evaluationService.searchEvalDealers(this.evalDealerControl.value ?? '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(options => this.evalDealerOptions.set(options));
  }

  // Event handlers
  updateStatus(option: EvaluationStatusSelectedOptions, event: MatCheckboxChange): void {
    if (!this.filter.statuses) {
      this.filter.statuses = [];
    }

    const key = this.getEnumKey(option, EvaluationStatusSelectedOptions);

    if (event.checked && !this.filter.statuses.includes(key)) {
      if (key === 'FAST_QUOTE') {
        this.filter.statuses.push('FAST_QUOTE_WITH_OFFER');
      }
      if (key === 'PRICING' && !this.filter.statuses.includes('RETURNED_PRICING')) {
        this.filter.statuses.push('RETURNED_PRICING');
      }
      this.filter.statuses.push(key);
    } else if (!event.checked) {
      this.filter.statuses = this.filter.statuses.filter(s => s !== key);
    }

    this.initStatusOptions();
    this.submitFilter();
  }

  changeSegment(segment: string): void {
    if (!this.filter.segments) {
      this.filter.segments = [];
    }

    const index = this.filter.segments.indexOf(segment);
    if (index >= 0) {
      this.filter.segments = this.filter.segments.filter(s => s !== segment);
    } else {
      this.filter.segments.push(segment);
    }

    this.submitFilter();
    this.initStatusOptions();
  }

  toggleType(type: string): void {
    if (this.isTypeSelected(type)) {
      this.filter.evalTypes = this.filter.evalTypes?.filter(t => t !== type);
    } else {
      this.filter.evalTypes = this.filter.evalTypes ?? [];
      this.filter.evalTypes.push(type);
    }
    this.submitFilter();
  }

  toggleOwnerType(type: string): void {
    if (this.isOwnerTypeSelected(type)) {
      this.filter.ownerTypes = this.filter.ownerTypes?.filter(t => t !== type);
    } else {
      this.filter.ownerTypes = this.filter.ownerTypes ?? [];
      this.filter.ownerTypes.push(type);
    }
    this.submitFilter();
  }

  toggleStatus(status: string): void {
    this.normalizeNegotiationStatuses();

    if (this.isStatusSelected(status)) {
      this.filter.negotiationStatuses = this.filter.negotiationStatuses?.filter(s => s !== status);
    } else {
      this.filter.negotiationStatuses = this.filter.negotiationStatuses ?? [];
      this.filter.negotiationStatuses.push(status);
    }
    this.submitFilter();
  }

  toggleFastQuoteStatus(status: string): void {
    this.disableConcurrentFastQuoteStatus(status);

    if (this.isStatusFastQuoteSelected(status)) {
      this.filter.fastQuoteStatuses = this.filter.fastQuoteStatuses?.filter(s => s !== status);
    } else {
      this.filter.fastQuoteStatuses = this.filter.fastQuoteStatuses ?? [];
      this.filter.fastQuoteStatuses.push(status);
    }
    this.submitFilter();
  }

  toggleRescueStatus(status: EvalRescueStatus): void {
    this.normalizeRescueStatuses();

    if (this.isRescueStatusSelected(status)) {
      this.filter.rescueStatuses = this.filter.rescueStatuses?.filter(s => s !== status) ?? [];
    } else {
      this.filter.rescueStatuses = this.filter.rescueStatuses ?? [];
      this.filter.rescueStatuses.push(status);
    }
    this.submitFilter();
  }

  selectUf(event: MatSelectChange): void {
    this.filter.ufs = event.value ?? [];
    this.ufControl.setValue(this.filter.ufs);

    if (this.cityControl.value) {
      this.cityControl.setValue('');
      this.filter.cityIds = [];
    }
    this.submitFilter();
  }

  removeUf(uf: string): void {
    this.filter.ufs = this.filter.ufs?.filter(u => u !== uf) ?? [];
    this.ufControl.setValue(this.filter.ufs);
    this.submitFilter();
  }

  selectCity(city: City): void {
    this.filter.cityIds = this.filter.cityIds ?? [];

    if (!this.filter.cityIds.includes(String(city.id))) {
      this.filter.cityIds.push(String(city.id));
      this.selectedCities.update(cities => [...cities, city]);
    }

    this.cityControl.setValue('');
    this.submitFilter();
  }

  removeCity(cityId: string): void {
    this.filter.cityIds = this.filter.cityIds?.filter(id => String(id) !== String(cityId)) ?? [];
    this.selectedCities.update(cities => cities.filter(c => c.id !== cityId));
    this.submitFilter();
  }

  applyPricingUserFilter(): void {
    const selectedIds = this.pricingUserControl.value ?? [];
    if (!selectedIds.length) {
      this.refreshPricingUserControl();
      return;
    }
    this.filter.pricingUserIds = selectedIds;
    this.submitFilter();
  }
  
  removePricingUser(userId: string): void {
    this.filter.pricingUserIds = this.filter.pricingUserIds?.filter(id => id !== userId);
    if (!this.filter.pricingUserIds?.length) {
      this.filter.pricingUserIds = undefined;
    }
    this.pricingUserControl.setValue(this.filter.pricingUserIds ?? []);
    this.submitFilter();
  }
  
  getPricingUserName(userId: string): string {
    return this.pricingUserOptions().find(u => u.id === userId)?.name ?? userId;
  }

  onChangeEvalDealerQuery(value: string | EvalDealerHelper | null): void {
    if (value && typeof value !== 'string') {
      this.selectEvalDealer(value);
      this.evalDealerControl.setValue(value.name);
    } else {
      this.refreshEvalDealerOptions();
    }
  }

  selectEvalDealer(evalDealer: EvalDealerHelper): void {
    this.filter.evalDealerIds = [evalDealer.id];
    this.evalDealerControl.setValue(evalDealer.name);
    this.submitFilter();
  }

  checkResetEvalDealer(): void {
    if (this.evalDealerControl.value === '' && this.filter.evalDealerIds?.length) {
      this.filter.evalDealerIds = undefined;
      this.submitFilter();
    }
  }

  private onChangeCityQuery(value: string): void {
    this.openService.searchCitiesByName(value)
      .pipe(
        debounceTime(300),
        map(cities => {
          const ufs = this.filter.ufs;
          if (ufs?.length) {
            return cities.filter(c => ufs.includes(c.uf));
          }
          return cities;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(cities => this.cityOptions.set(cities));
  }

  submitFilter(): void {
    this.filterChanged.emit(this.filter);
  }

  onSearchEnter(event: Event): void {
    (event.target as HTMLInputElement | null)?.blur();
  }

  // Helper methods
  isTypeSelected(type: string): boolean {
    return this.filter.evalTypes?.includes(type) ?? false;
  }

  isOwnerTypeSelected(type: string): boolean {
    return this.filter.ownerTypes?.includes(type) ?? false;
  }

  isStatusSelected(status: string): boolean {
    this.normalizeNegotiationStatuses();
    return this.filter.negotiationStatuses?.includes(status) ?? false;
  }

  isStatusFastQuoteSelected(status: string): boolean {
    this.normalizeFastQuoteStatuses();
    return this.filter.fastQuoteStatuses?.includes(status) ?? false;
  }

  isRescueStatusSelected(status: EvalRescueStatus): boolean {
    this.normalizeRescueStatuses();
    return this.filter.rescueStatuses?.includes(status) ?? false;
  }

  isSegmentSelected(segment: string): boolean {
    return this.filter.segments?.includes(segment) ?? false;
  }

  getEvalTypeLabel(type: string): string {
    return EvalTypeLabel.getLabel(type);
  }

  getOwnerTypeLabel(type: string): string {
    return OwnerTypeLabel.getLabel(type);
  }

  getNegotiationStatus(status: string): string {
    return NegotiationStatusTypes.getLabel(status);
  }

  getFastQuoteStatus(status: string): string {
    return FastQuoteStatus.getLabel(status);
  }

  private getEnumKey(enumValue: EvaluationStatusSelectedOptions, enumType: typeof EvaluationStatusSelectedOptions): string {
    const idx = Object.values(enumType).indexOf(enumValue);
    return Object.keys(enumType)[idx];
  }

  private normalizeNegotiationStatuses(): void {
    if (typeof this.filter.negotiationStatuses === 'string') {
      this.filter.negotiationStatuses = [this.filter.negotiationStatuses];
    }
  }

  private normalizeFastQuoteStatuses(): void {
    if (typeof this.filter.fastQuoteStatuses === 'string') {
      this.filter.fastQuoteStatuses = [this.filter.fastQuoteStatuses];
    }
  }

  private normalizeRescueStatuses(): void {
    if (typeof this.filter.rescueStatuses === 'string') {
      this.filter.rescueStatuses = [this.filter.rescueStatuses];
    }
  }

  private disableConcurrentFastQuoteStatus(status: string): void {
    if (this.filter.fastQuoteStatuses) {
      if (this.allFastQuoteOfferStatus.includes(status)) {
        this.filter.fastQuoteStatuses = this.filter.fastQuoteStatuses.filter(s => !this.allFastQuoteNoOfferStatus.includes(s));
      } else {
        this.filter.fastQuoteStatuses = this.filter.fastQuoteStatuses.filter(s => !this.allFastQuoteOfferStatus.includes(s));
      }
    }
  }

  clearPeriodRange(): void {
    this.filter.updatedAtIni = undefined;
    this.filter.updatedAtEnd = undefined;
    this.filter.pageControl.page = 0;
    this.selectedDateRange = null;
    this.periodPicker?.clear();
    this.submitFilter();
  }

  selectedDate(event: { startDate?: Dayjs | null; endDate?: Dayjs | null }): void {
    if (!event?.startDate || !event?.endDate) {
      return;
    }
    this.setRange(event.startDate, event.endDate);
    this.refreshPeriodRange();
    this.submitFilter();
  }

  setRange(start: Dayjs, end: Dayjs): void {
    this.filter.updatedAtIni = start.startOf('day').format();
    this.filter.updatedAtEnd = end.startOf('day').add(1, 'day').format();
    this.filter.pageControl.page = 0;
  }

  private refreshPeriodRange(): void {
    if (this.filter.updatedAtIni && this.filter.updatedAtEnd) {
      const startDate = dayjs(this.filter.updatedAtIni);
      const endDate = dayjs(this.filter.updatedAtEnd).subtract(1, 'day');
      this.selectedDateRange = { startDate, endDate };
    } else {
      this.selectedDateRange = null;
    }
  }

  hasResyncIntegrationErrorPermission(): boolean {
    const user = this.userInfo();
    if (!user) {
      return false;
    }

    return user.isAdmin() || user.hasAnyPermission(Permission.RESYNC_EVALUATION_INTEGRATION);
  }

}
